import React from 'react';
import { Typography, Tabs, Tab, IconButton, Collapse } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import LoadableMonacoEditor from 'js/components/elements/editors/monaco/LoadableMonacoEditor';
import { ChipBadge } from 'new/elements/ChipBadge';
import { ParameterSection } from './ParameterSection';
import { ResponseHeadersTable } from './ApiRequestTables';
import { UIState, ResponseState, ValidationErrors } from '../hooks/useApiRequestForm';
import { Parameter, Header } from '../hooks/useParameterManager';
import styles from '../ApiRequestModal.module.scss';

interface TestPanelProps {
  mode: 'config' | 'test';
  pathParameters: Parameter[];
  queryParameters: Parameter[];
  headers: Header[];
  rawJsonBody?: string;
  method: string;
  uiState: UIState;
  responseState: ResponseState;
  validationErrors: ValidationErrors;
  onParameterUpdate: (
    id: number,
    field: string,
    value: string | boolean,
    type: 'path' | 'query' | 'body',
  ) => void;
  onParameterRemove: (id: number, type: 'path' | 'query' | 'body') => void;
  onParameterAdd: (type: 'path' | 'query' | 'body') => void;
  onHeaderUpdate: (id: number, field: string, value: string | boolean) => void;
  onHeaderRemove: (id: number) => void;
  onHeaderAdd: () => void;
  onToggleSection: (section: keyof UIState) => void;
  onJsonBodyChange: (value: string) => void;
  onResponseTabChange: (tab: string) => void;
}

const EDITOR_HEIGHT = 300;
const RESPONSE_EDITOR_HEIGHT = 400;

const getStatusColor = (status: string | null): 'danger' | 'success' | 'warning' | 'primary' => {
  if (!status || status === 'Error') return 'danger';
  const statusCode = parseInt(status.split(' ')[0]);
  if (statusCode >= 200 && statusCode < 300) return 'success';
  if (statusCode >= 300 && statusCode < 400) return 'warning';
  if (statusCode >= 400) return 'danger';
  return 'primary';
};

export const TestPanel: React.FC<TestPanelProps> = ({
  mode,
  pathParameters,
  queryParameters,
  headers,
  rawJsonBody,
  method,
  uiState,
  responseState,
  validationErrors,
  onParameterUpdate,
  onParameterRemove,
  onParameterAdd,
  onHeaderUpdate,
  onHeaderRemove,
  onHeaderAdd,
  onToggleSection,
  onJsonBodyChange,
  onResponseTabChange,
}) => {
  return (
    <>
      <div className={styles.leftPanel}>
        <div className={styles.parametersHeader}>
          <Typography variant="overline">Request Parameters</Typography>
        </div>
        <ParameterSection
          mode={mode}
          title="Path Parameters"
          type="path"
          parameters={pathParameters}
          validationErrors={validationErrors.pathParameters}
          expanded={uiState.pathParamsOpen}
          onToggle={() => onToggleSection('pathParamsOpen')}
          onUpdate={onParameterUpdate}
          onRemove={onParameterRemove}
          onAdd={() => onParameterAdd('path')}
        />
        <ParameterSection
          mode={mode}
          title="Query Parameters"
          type="query"
          parameters={queryParameters}
          validationErrors={validationErrors.queryParameters}
          expanded={uiState.queryParamsOpen}
          onToggle={() => onToggleSection('queryParamsOpen')}
          onUpdate={onParameterUpdate}
          onRemove={onParameterRemove}
          onAdd={() => onParameterAdd('query')}
        />
        <ParameterSection
          mode={mode}
          title="Headers"
          type="headers"
          headers={headers}
          validationErrors={validationErrors.headers}
          expanded={uiState.headersOpen}
          onToggle={() => onToggleSection('headersOpen')}
          onUpdate={onHeaderUpdate}
          onRemove={onHeaderRemove}
          onAdd={onHeaderAdd}
        />
        {method !== 'GET' && (
          <div>
            <div className={styles.sectionHeader} onClick={() => onToggleSection('bodyOpen')}>
              <IconButton
                size="small"
                className={`${styles.expandIcon} ${uiState.bodyOpen ? styles.expanded : ''}`}
              >
                <ExpandMoreIcon />
              </IconButton>
              <Typography variant="caption">Request Body</Typography>
            </div>
            <Collapse in={uiState.bodyOpen}>
              <div className={styles.sectionContent}>
                <div className={styles.editorWrapper}>
                  <LoadableMonacoEditor
                    language="json"
                    theme="dark"
                    value={rawJsonBody}
                    height={EDITOR_HEIGHT}
                    onChange={(value) => onJsonBodyChange(value || '')}
                    options={{
                      readOnly: false,
                      lineNumbers: 'on',
                      minimap: { enabled: false },
                      scrollBeyondLastLine: false,
                      fontSize: 12,
                    }}
                  />
                </div>
              </div>
            </Collapse>
          </div>
        )}
      </div>
      <div className={styles.rightPanel}>
        <div className={styles.responseHeader}>
          <Typography variant="overline">Response</Typography>
          <div className={styles.statusBadges}>
            {responseState.responseStatus && (
              <ChipBadge
                label={responseState.responseStatus}
                color={getStatusColor(responseState.responseStatus)}
                variant="outlined"
              />
            )}
          </div>
        </div>
        <div className={styles.responseContent}>
          <Tabs
            className={styles.responseTabs}
            value={responseState.responseTab}
            onChange={(e, newValue) => onResponseTabChange(newValue)}
          >
            <Tab label="Body" value="body" />
            <Tab label="Headers" value="headers" />
          </Tabs>
          <div className={styles.responseBody}>
            {responseState.responseTab === 'body' && (
              <div className={styles.editorWrapper}>
                <LoadableMonacoEditor
                  language="json"
                  theme="dark"
                  value={responseState.responseData}
                  height={RESPONSE_EDITOR_HEIGHT}
                  onChange={() => {}}
                  options={{
                    readOnly: true,
                    lineNumbers: 'on',
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    fontSize: 12,
                  }}
                />
              </div>
            )}
            {responseState.responseTab === 'headers' && (
              <div className={styles.tableWrapper}>
                <ResponseHeadersTable headers={responseState.responseHeaders} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
